import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  DestroyRef,
  ElementRef,
  forwardRef,
  inject,
  input,
  InputSignal,
  OnInit,
  signal,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  ControlValueAccessor,
  FormControl,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
  ValidationErrors,
  Validator,
} from '@angular/forms';
import {
  CountryISO,
  NgxIntlTelInputComponent,
  NgxIntlTelInputModule,
  PhoneNumberFormat,
  SearchCountryField,
} from 'ngx-intl-tel-input';
import { NgClass } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { ViewportService } from '../../services/viewport.service';
import { UiBottomSheetComponent } from '../ui-bottom-sheet/ui-bottom-sheet.component';

export interface PhoneNumberValue {
  number: string;
  internationalNumber: string;
  nationalNumber: string;
  e164Number: string;
  countryCode: string;
  dialCode: string;
}

interface Country {
  name: string;
  iso2: string;
  dialCode: string;
  priority: number;
  areaCodes?: string[];
  htmlId: string;
  flagClass: string;
  placeHolder: string;
}

@Component({
  selector: 'ui-phone-input-v2',
  imports: [
    NgxIntlTelInputModule,
    ReactiveFormsModule,
    UiBottomSheetComponent,
    NgClass,
    TranslateModule,
  ],
  templateUrl: './ui-phone-input-v2.component.html',
  styleUrl: './ui-phone-input-v2.component.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => UiPhoneInputV2Component),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => UiPhoneInputV2Component),
      multi: true,
    },
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UiPhoneInputV2Component
  implements ControlValueAccessor, OnInit, AfterViewInit, Validator
{
  public required: InputSignal<boolean> = input(false);
  private readonly destroyRef = inject(DestroyRef);
  private readonly elementRef = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly viewport = inject(ViewportService);
  private readonly changeDetectorRef = inject(ChangeDetectorRef);

  @ViewChild(NgxIntlTelInputComponent)
  private intlTelInput!: NgxIntlTelInputComponent;

  public control = new FormControl();
  public countrySearchControl = new FormControl('');

  public readonly separateDialCode = true;
  public readonly SearchCountryField = SearchCountryField;
  public readonly CountryISO = CountryISO;
  public readonly PhoneNumberFormat = PhoneNumberFormat;

  public sheetOpen = signal(false);
  public selectedCountryIso = signal('us');

  public filteredCountries = computed(() => {
    const search = this.countrySearch().toLowerCase();
    const countries = this.allCountries();
    if (!search) {
      return countries;
    }
    return countries.filter(
      (c) =>
        c.name.toLowerCase().includes(search) || c.dialCode.includes(search),
    );
  });

  private countrySearch = signal('');
  private allCountries = signal<Country[]>([]);

  private onChange: (val: string | null) => void = () => undefined;
  private onTouched: () => void = () => undefined;
  private onValidatorChange: () => void = () => undefined;

  public ngOnInit(): void {
    this.control.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((value: PhoneNumberValue | null) => {
        this.onChange(value?.e164Number ?? null);
        this.onValidatorChange();
        this.dropDuplicatedDialCode(value);
      });

    this.countrySearchControl.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((value) => {
        this.countrySearch.set(value ?? '');
      });
  }

  public ngAfterViewInit(): void {
    if (this.intlTelInput) {
      this.allCountries.set(this.intlTelInput.allCountries);
      this.selectedCountryIso.set(
        this.intlTelInput.selectedCountry?.iso2 || 'us',
      );
    }

    const flagContainer: HTMLElement | null =
      this.elementRef.nativeElement.querySelector<HTMLElement>(
        '.iti__flag-container',
      );

    if (flagContainer) {
      flagContainer.addEventListener(
        'click',
        (event: Event) => {
          if (this.viewport.isMobile()) {
            event.stopPropagation();
            event.preventDefault();
            this.openCountrySheet();
          }
        },
        true,
      );
    }
  }

  public openCountrySheet(): void {
    if (this.intlTelInput) {
      this.allCountries.set(this.intlTelInput.allCountries);
      this.selectedCountryIso.set(
        this.intlTelInput.selectedCountry?.iso2 || 'us',
      );
    }
    this.countrySearchControl.setValue('');
    this.sheetOpen.set(true);
  }

  public closeCountrySheet(): void {
    this.sheetOpen.set(false);
    this.countrySearchControl.setValue('');
  }

  public onCountryTap(country: Country): void {
    if (!this.intlTelInput) {
      return;
    }
    const focusableEl: HTMLInputElement | null =
      this.elementRef.nativeElement.querySelector<HTMLInputElement>(
        '.iti__tel-input',
      );

    if (!focusableEl) {
      return;
    }

    this.intlTelInput.onCountrySelect(country, focusableEl);
    this.selectedCountryIso.set(country.iso2);
    this.closeCountrySheet();
  }

  public writeValue(value: string | null): void {
    this.control.setValue(value);
  }

  public registerOnChange(fn: (value: string | null) => void): void {
    this.onChange = fn;
  }

  public registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  public setDisabledState?(isDisabled: boolean): void {
    if (isDisabled) {
      this.control.disable();
    } else {
      this.control.enable();
    }
  }

  public validate(): ValidationErrors | null {
    return this.control.errors;
  }

  public registerOnValidatorChange(fn: () => void): void {
    this.onValidatorChange = fn;
  }

  private dropDuplicatedDialCode(value: PhoneNumberValue | null): void {
    if (!this.separateDialCode || !this.intlTelInput || !value?.number) {
      return;
    }

    const displayedNumber: string | undefined = this.intlTelInput.phoneNumber;

    if (
      !displayedNumber?.trimStart().startsWith('+') ||
      value.number.startsWith('+')
    ) {
      return;
    }

    this.intlTelInput.phoneNumber = value.number;
    this.changeDetectorRef.markForCheck();
  }
}
